import {
  EAssistantMarketplaceScope,
  type IAssistant,
  type IMarketplaceAssistant,
  type IPublishAssistantPayload,
} from "../types/assistant";
import type {
  IAssistantMarketplacePermissionsResult,
  IBuildMarketplaceProjectOptionsProps,
  ICanBrowseAssistantMarketplaceProps,
  IIsAssistantFromOtherProjectMarketplaceProps,
  IMarketplaceProjectOption,
  IMergeMarketplaceAssistantsByUuidProps,
  IPublishAssistantDraft,
  IResolveAssistantMarketplacePermissionsProps,
} from "../types/assistant-marketplace";

// NOTE: NSAI-3830 / NSAI-5274 — the organization assistant marketplace rules,
// shared by web and mobile. Everything here is the platform-agnostic half of the
// feature; each client keeps only reading its own auth layer and drawing the UI.

// NOTE: True when the assistant reached this project through a marketplace
// listing owned by ANOTHER project — the current project can use it but never
// manage it. GET /assistants leaves `marketplace` null on an assistant published
// from the current project, so a non-null block alone does not make it foreign.
export const isAssistantFromOtherProjectMarketplace = ({
  assistant,
  currentProjectUuid,
}: IIsAssistantFromOtherProjectMarketplaceProps): boolean =>
  assistant?.marketplace != null &&
  assistant.marketplace.sourceProjectUuid !== currentProjectUuid;

// NOTE: Whether the marketplace can be browsed here — the Marketplace tab and
// the browse query both read it. Two things the raw role payload does not say:
// - `marketplace_assistant:write` implies `:read` on the server
//   (PERMISSION_IMPLIES), but the payload the clients receive is not expanded,
//   so a write-only curator (a SAML mapping, a role predating the editor's
//   write-needs-read rule) must be let in explicitly, or they could not load
//   the listing they need to manage their own publication.
// - The marketplace is organization-only: a personal project has nothing to
//   browse, whatever the Admin wildcard covers.
// Same rule the automation marketplace applies (NSAI-5273,
// `useCanBrowseMarketplace`).
export const canBrowseAssistantMarketplace = ({
  isPersonalProject,
  hasMarketplaceAssistantReadPermission,
  hasMarketplaceAssistantWritePermission,
}: ICanBrowseAssistantMarketplaceProps): boolean =>
  !isPersonalProject &&
  (hasMarketplaceAssistantReadPermission ||
    hasMarketplaceAssistantWritePermission);

// NOTE: Publish / manage-listing is offered only to marketplace_assistant:write
// holders, and only for assistants that live in the CURRENT project. App
// assistants are excluded — they are global already, and so is every assistant
// of a personal project, which the server refuses to publish
// (`personal_project_not_allowed`). A listing surfaced from
// another project is read-only here, exactly like an app assistant: every
// action that writes to it is hidden, including the NSAI-4775 visibility flip,
// which the server would refuse anyway since the assistant lives elsewhere.
export const resolveAssistantMarketplacePermissions = ({
  assistant,
  currentProjectUuid,
  isPersonalProject,
  permissions,
  hasMarketplaceAssistantWritePermission,
}: IResolveAssistantMarketplacePermissionsProps): IAssistantMarketplacePermissionsResult => {
  const isFromOtherProjectMarketplace = isAssistantFromOtherProjectMarketplace({
    assistant,
    currentProjectUuid,
  });

  const isHiddenPublish =
    !assistant ||
    assistant.isAppAssistant ||
    isPersonalProject ||
    isFromOtherProjectMarketplace ||
    !hasMarketplaceAssistantWritePermission;

  if (!isFromOtherProjectMarketplace) {
    return { ...permissions, isHiddenPublish };
  }

  return {
    ...permissions,
    isHiddenEdit: true,
    isHiddenDelete: true,
    isHiddenShare: true,
    isHiddenToggleVisibility: true,
    isHiddenPublish,
  };
};

// NOTE: The dialog's starting state — the existing listing when there is one,
// otherwise an organization-wide, non-default publish. Rebuilt on every open so
// a reopened dialog never shows the draft of the previous session.
export const buildPublishAssistantDraft = (
  listing: IMarketplaceAssistant | null,
): IPublishAssistantDraft => ({
  accessScope:
    listing?.marketplace.accessScope ?? EAssistantMarketplaceScope.ORGANIZATION,
  isDefault: listing?.marketplace.isDefault ?? false,
  targetProjectUuids: listing?.marketplace.targetProjectUuids ?? [],
});

// NOTE: The server rejects `isDefault` outside the organization scope
// (`default_requires_org_scope`) and a selected-projects scope without targets,
// so each scope sends only the fields that belong to it.
export const buildPublishAssistantPayload = ({
  accessScope,
  isDefault,
  targetProjectUuids,
}: IPublishAssistantDraft): IPublishAssistantPayload =>
  accessScope === EAssistantMarketplaceScope.ORGANIZATION
    ? { accessScope, isDefault }
    : { accessScope, targetProjectUuids };

export const isPublishAssistantDraftSubmittable = ({
  accessScope,
  targetProjectUuids,
}: IPublishAssistantDraft): boolean =>
  accessScope === EAssistantMarketplaceScope.ORGANIZATION ||
  targetProjectUuids.length > 0;

// NOTE: GET /assistants marks `marketplace` only on listings merged from OTHER
// projects, so an assistant published from the CURRENT project reads its
// published state from the marketplace browse list instead: a listing whose
// uuid matches the assistant. The map makes that an O(1) lookup per row.
export const buildMarketplaceListingMap = (
  listings: IMarketplaceAssistant[] | null | undefined,
): Map<string, IMarketplaceAssistant> => {
  const listingByAssistantUuid = new Map<string, IMarketplaceAssistant>();

  for (const listing of listings ?? []) {
    listingByAssistantUuid.set(listing.uuid, listing);
  }

  return listingByAssistantUuid;
};

// NOTE: NSAI-5998 — the current project is offered like any other target and
// sorted first. Picking it alone is the only way to publish an assistant to the
// members of its own project without also exposing it to a sibling project: a
// role with private_assistant:read sees only what it owns or was shared, so "it
// already lives here" does not make it reachable.
export const buildMarketplaceProjectOptions = ({
  projects,
  currentProjectUuid,
  currentProjectHint,
}: IBuildMarketplaceProjectOptionsProps): IMarketplaceProjectOption[] => {
  if (!projects) return [];

  const options = projects.map(
    (project): IMarketplaceProjectOption => ({
      uuid: project.uuid,
      title: project.title,
      ...(project.uuid === currentProjectUuid
        ? { hint: currentProjectHint }
        : {}),
    }),
  );

  return options.sort(
    (first, second) =>
      Number(second.uuid === currentProjectUuid) -
      Number(first.uuid === currentProjectUuid),
  );
};

// NOTE: Non-default marketplace assistants are not part of the regular project
// list, yet "start a chat" from the marketplace tab must still resolve them.
// The marketplace entries go in first so the project-scoped entry wins when an
// assistant appears in both.
export const mergeMarketplaceAssistantsByUuid = ({
  assistants,
  marketplaceAssistants,
}: IMergeMarketplaceAssistantsByUuidProps): Record<string, IAssistant> => {
  const byUuid: Record<string, IAssistant> = {};

  for (const assistant of marketplaceAssistants ?? []) {
    byUuid[assistant.uuid] = assistant;
  }
  for (const assistant of assistants) {
    byUuid[assistant.uuid] = assistant;
  }

  return byUuid;
};
